import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { SAVE_QUOTATION, SELECT_QUOTATION, UPDATE_QUOTATION_LOCK } from '../mutations';
import { QUOTATION_SAVED, CONVENTION_QUOTATION_RETRIEVED, QUOTATION_LOCK_UPDATED } from '../subscriptions';
import { get, set, sequence, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useApolloClient } from '@apollo/client';
import { useMutation } from 'features/common/security/hooks';
import { useToast, useSimpleError, useLocalSubscription, useAuthorizations, useDocumentId } from 'hooks';
import { useBackdrop } from 'hooks/useBackdrop';
import { useTranslation } from 'react-i18next';
import { tooltipRef, getQuotationValidator } from '../quotationsUtils';
import { showModifiedQuotationMs } from 'constants/defaultValues';
import { cascoQuotationBase } from 'features/insuranceDocument/queries/fragments';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { Prompt } from 'react-router-dom';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import {
  allowAddQuotationsVar,
  allowQuotationLockVar,
  currentOfferStateCodeVar,
  customQuotationModalOpenVar,
  editingQuotationIdVar,
  hasHistoryVar,
  headerDataVar,
  lastModifiedQuotationVar,
  offerInNegotiationStateVar
} from '../variables';
import { useReactiveVar } from '@apollo/client';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const QuotationContainer = ({ index, data, ItemComponent, selected, computing }) => {
  const { documentId } = useDocumentId();

  const [saving, setSaving] = useState(false);
  const [lockUpdating, setLockUpdating] = useState(false);
  const [showTitleTooltip, setShowTitleTooltip] = useState(false);
  const [showMessageTooltip, setShowMessageTooltip] = useState(false);

  const headerData = useReactiveVar(headerDataVar);
  const currentOfferStateCode = useReactiveVar(currentOfferStateCodeVar);
  const offerInNegotiationState = useReactiveVar(offerInNegotiationStateVar);

  const quotationData = useMemo(() => {
    if (data.sourceQuotationId)
      return {
        ...data,
        currencyCode: headerData?.currencyCode,
        isOfferInNegotiation: currentOfferStateCode === offerInNegotiationState,
        negotiatedOffer: currentOfferStateCode !== offerInNegotiationState
      };
    return {
      ...data,
      currencyCode: headerData?.currencyCode,
      isOfferInNegotiation: false,
      negotiatedOffer: false
    };
  }, [data, headerData, currentOfferStateCode, offerInNegotiationState]);

  const [quotationLens, quotationDirtyInfo, resetQuotationLens] = useChangeTrackingLens(quotationData);

  const { t } = useTranslation();
  const showError = useSimpleError();
  const addToast = useToast();
  const { Backdrop, showBackdrop, hideBackdrop } = useBackdrop();
  const client = useApolloClient();
  const { globalReadOnly, documentTypeId, documentStateId } = useContext(InsuranceDataContext);

  const lastModifiedQuotationId = useReactiveVar(lastModifiedQuotationVar);
  const allowAddQuotations = useReactiveVar(allowAddQuotationsVar);
  const allowQuotationLock = useReactiveVar(allowQuotationLockVar);

  const authorizations = useAuthorizations(
    [
      AuthorizationCodes.hasRightToSaveEmptyAdditionalData,
      AuthorizationCodes.hasRightToSelectQuotation,
      AuthorizationCodes.hasRightToRecomputeQuotation
    ],
    documentTypeId,
    documentStateId
  );

  const quotationValidator = useMemo(
    () => getQuotationValidator(authorizations?.hasRightToSaveEmptyAdditionalData),
    [authorizations?.hasRightToSaveEmptyAdditionalData]
  );
  const [validation, validate] = useDirtyFieldValidation(quotationValidator);

  useEffect(() => {
    validate(quotationLens |> get, quotationDirtyInfo);
  }, [quotationLens, validate, quotationDirtyInfo]);

  useEffect(() => {
    if (!quotationData) return;
    resetQuotationLens(quotationData);
  }, [quotationData, resetQuotationLens]);

  // #region Save & Retry

  const [saveQuotation] = useMutation(SAVE_QUOTATION, {
    onError: error => {
      setSaving(false);
      showError(error);
    }
  });

  const handleSave = useCallback(() => {
    const quotation = quotationLens |> get;
    if (!validate(quotation)) {
      return;
    }

    lastModifiedQuotationVar(quotation.quotationId);
    setSaving(true);

    if (quotation.sourceQuotationId === null) {
      resetQuotationLens(prev => ({ ...prev, insuranceAmount: null, totalInsuranceAmount: null, errorMessage: null }));
    }
    saveQuotation({
      variables: {
        input: {
          documentId,
          quotationId: quotation.quotationId,
          conventionId: quotation.conventionId,
          insurerId: quotation.insurerId,
          period: quotation.period,
          periodicity: quotation.periodicity,
          monthNo: quotation.monthNo,
          suplementPeriod: quotation.suplementPeriod,
          totalDamageDeductiblePercent: quotation.totalDamageDeductiblePercent,
          partialDamageDeductibleAmount: quotation.partialDamageDeductibleAmount,
          discountPercent: quotation.discountPercent,
          discountAmount: quotation.discountAmount,
          insuranceAmount: quotation.insuranceAmount,
          totalInsuranceAmount: quotation.totalInsuranceAmount,
          insuredAmount: quotation.insuredAmount,
          risks: quotation.risks.map(risk => ({
            riskTypeId: risk.riskTypeId,
            selected: risk.selected
          })),
          coefficients: quotation.coefficients.map(coefficient => ({
            additionalDataTypeId: coefficient.additionalDataTypeId,
            details: coefficient.details.map(detail => ({
              detailId: detail.detailId,
              selected: detail.selected
            }))
          }))
        }
      }
    });
  }, [validate, quotationLens, saveQuotation, documentId, resetQuotationLens]);

  const handleRetry = useCallback(() => {
    resetQuotationLens(prev => ({ ...prev, errorMessage: null }));

    const quotation = quotationLens |> get;
    lastModifiedQuotationVar(quotation.quotationId);

    saveQuotation({
      variables: {
        input: {
          documentId,
          quotationId: quotation.quotationId,
          conventionId: quotation.conventionId,
          insurerId: quotation.insurerId,
          period: quotation.period,
          monthNo: quotation.monthNo,
          totalDamageDeductiblePercent: quotation.totalDamageDeductiblePercent,
          partialDamageDeductibleAmount: quotation.partialDamageDeductibleAmount,
          discountPercent: quotation.discountPercent,
          discountAmount: quotation.discountAmount,
          periodicity: quotation.periodicity,
          insuredAmount: quotation.insuredAmount,
          risks: quotation.risks.map(risk => ({
            riskTypeId: risk.riskTypeId,
            selected: risk.selected
          })),
          coefficients: quotation.coefficients.map(coefficient => ({
            additionalDataTypeId: coefficient.additionalDataTypeId,
            details: coefficient.details.map(detail => ({
              detailId: detail.detailId,
              selected: detail.selected
            }))
          }))
        }
      }
    });
  }, [resetQuotationLens, quotationLens, saveQuotation, documentId]);

  // #endregion

  // #region Select

  const [selectQuotation] = useMutation(SELECT_QUOTATION, {
    onError: error => {
      hideBackdrop();
      showError(error);
    }
  });

  const handleSelect = useCallback(() => {
    showBackdrop();
    selectQuotation({
      variables: {
        input: {
          quotationId: quotationLens.quotationId |> get,
          documentId: documentId
        }
      }
    });
  }, [showBackdrop, selectQuotation, quotationLens, documentId]);

  // #endregion

  // #region Lock

  const [updateQuotationLock] = useMutation(UPDATE_QUOTATION_LOCK, {
    onError: error => showError(error)
  });

  const handleQuotationLockChange = useCallback(
    lockedYN => {
      (quotationLens.lockedYN |> set)(lockedYN);
      setLockUpdating(true);
      updateQuotationLock({
        variables: {
          input: {
            documentId,
            quotationId: quotationLens.quotationId |> get,
            lockedYN
          }
        }
      });
    },
    [updateQuotationLock, documentId, quotationLens]
  );

  // #endregion

  // #region Subscriptions

  useLocalSubscription(QUOTATION_SAVED, {
    variables: { quotationId: quotationLens.quotationId |> get },
    skip: !(quotationLens.quotationId |> get),
    onData: ({ data }) => {
      if (data?.cascoQuotationSaved?.finishedRetrieval) {
        setSaving(false);
        hasHistoryVar(true);
        addToast(t('General.Saved'), 'success', { toastId: 'QuotationSavedToast' });

        setTimeout(() => {
          lastModifiedQuotationVar(null);
        }, showModifiedQuotationMs);
      }
    }
  });

  useLocalSubscription(CONVENTION_QUOTATION_RETRIEVED, {
    variables: { quotationId: quotationLens.quotationId |> get },
    onData: ({ data }) => {
      setSaving(false);
      hasHistoryVar(true);
      addToast(t('General.Saved'), 'success', { toastId: 'savedMessageToast' });
      if (data?.conventionQuotationRetrieved?.silentMode === false)
        addToast(t('Offer.Edit.Casco.ConventionComputed'), 'success', { toastId: 'conventionComputedToast' });

      setTimeout(() => {
        lastModifiedQuotationVar(null);
      }, showModifiedQuotationMs);
    }
  });

  useLocalSubscription(QUOTATION_LOCK_UPDATED, {
    variables: { quotationId: quotationLens.quotationId |> get },
    onData: ({ data }) => {
      const cacheId = 'CascoQuotationItem:{"quotationId":' + data.cascoQuotationLockUpdated.quotationId + '}';
      const cascoQuotation = client.cache.data.data[cacheId];

      if (cascoQuotation) {
        client.cache.writeFragment({
          id: cacheId,
          fragment: cascoQuotationBase,
          fragmentName: 'cascoQuotationBase',
          data: {
            ...cascoQuotation,
            lockedYN: data.cascoQuotationLockUpdated.lockedYN
          }
        });
        client.cache.gc();
      }

      setLockUpdating(false);
    }
  });

  // #endregion

  // #region Handlers

  const handleAmountChange = useCallback(
    insuranceAmount => {
      (quotationLens.insuranceAmount |> set)(insuranceAmount || null);
      (quotationLens.totalInsuranceAmount |> set)((insuranceAmount / 12) * (quotationLens.period |> get));
    },
    [quotationLens]
  );

  const handleHeaderPropertyChange = useCallback(
    prop => value => {
      (quotationLens[prop] |> set)(value || null);
      if (value && prop == 'periodicity') {
        (quotationLens.monthNo |> set)(Math.floor((quotationLens.period |> get) / value));
        (quotationLens.suplementPeriod |> set)((quotationLens.period |> get) % value);
      }
    },
    [quotationLens]
  );

  const handleRiskSelectionChange = useCallback(
    id => value => {
      (quotationLens.risks |> sequence).forEach(risk => (risk.quotationRiskId |> get) === id && (risk.selected |> set)(value));
    },
    [quotationLens]
  );

  const handleCoefficientsSelectionChange = useCallback(
    (selectedCoefficientDetails, additionalDataTypeId) => {
      (quotationLens.coefficients |> sequence).forEach(
        coefficient =>
          (coefficient.additionalDataTypeId |> get) === additionalDataTypeId &&
          (coefficient.details |> sequence).forEach(detail =>
            coefficient.allowMultipleDetails |> get
              ? selectedCoefficientDetails.includes(detail.detailId |> get)
                ? (detail.selected |> set)(true)
                : (detail.selected |> set)(false)
              : (detail.detailId |> get) === selectedCoefficientDetails
              ? (detail.selected |> set)(true)
              : (detail.selected |> set)(false)
          )
      );
    },
    [quotationLens]
  );

  const handleEditCustomQuotation = useCallback(id => {
    editingQuotationIdVar(id);
    customQuotationModalOpenVar(true);
  }, []);

  // #endregion

  // #region Memos

  const titleRef = useMemo(() => tooltipRef(setShowTitleTooltip), []);
  const messageRef = useMemo(() => tooltipRef(setShowMessageTooltip), []);

  const selectDisabled = useMemo(() => selected || !(quotationLens.insuranceAmount |> get), [selected, quotationLens]);

  const colorTheme = useMemo(() => {
    if (!quotationLens) return;
    if (quotationLens.isOfferInNegotiation |> get) return 'info';
    if (quotationLens.negotiatedOffer |> get) return 'gray';
    return 'theme';
  }, [quotationLens]);

  const isDirty = useMemo(() => checkDirty(quotationDirtyInfo), [quotationDirtyInfo]);

  // #endregion

  // #region Read only check functions

  const disabled = useCallback(
    controlName =>
      globalReadOnly({ controlName, checkQuotationsStatus: { processingStatus: true, expiredStatus: true } }) ||
      computing ||
      (quotationLens.sourceQuotation |> get) ||
      (quotationLens.manualQuotation |> get) ||
      !(quotationLens.isActive |> get) ||
      (quotationLens.lockedYN |> get) ||
      lockUpdating ||
      saving,
    [globalReadOnly, computing, quotationLens, saving, lockUpdating]
  );

  const allowEditForNegotiation = useCallback(
    controlName =>
      !globalReadOnly({ controlName, checkQuotationsStatus: { processingStatus: true, expiredStatus: true } }) &&
      !computing &&
      (quotationLens.sourceQuotationId |> get) !== null &&
      (quotationLens.isOfferInNegotiation |> get) &&
      (quotationLens.isActive |> get) &&
      !(quotationLens.manualQuotation |> get) &&
      !saving,
    [globalReadOnly, computing, quotationLens, saving]
  );

  const allowSelection = useMemo(
    () =>
      !globalReadOnly({
        controlName: 'Comparator.SelectOffer',
        checkQuotationsStatus: { processingStatus: true, expiredStatus: true },
        hasAuthorization: authorizations?.hasRightToSelectQuotation
      }) &&
      !computing &&
      !(quotationLens.sourceQuotation |> get) &&
      ((quotationLens.isActive |> get) || (quotationLens.manualQuotation |> get)) &&
      !(quotationLens.lockedYN |> get) &&
      !isDirty &&
      !selectDisabled,
    [globalReadOnly, authorizations?.hasRightToSelectQuotation, computing, quotationLens, isDirty, selectDisabled]
  );

  const allowCustomQuotationEdit = useMemo(
    () =>
      !globalReadOnly({
        controlName: 'Comparator.EditQuotation',
        checkQuotationsStatus: { processingStatus: true, expiredStatus: true }
      }) &&
      !computing &&
      (quotationLens.manualQuotation |> get) &&
      !(quotationLens.lockedYN |> get) &&
      allowAddQuotations &&
      !saving,
    [globalReadOnly, computing, quotationLens, allowAddQuotations, saving]
  );

  const allowRetry = useMemo(
    () =>
      !globalReadOnly({
        controlName: 'Comparator.RetryCASCOQuotation',
        checkQuotationsStatus: { processingStatus: true, expiredStatus: true },
        hasAuthorization: authorizations?.hasRightToRecomputeQuotation
      }) &&
      !(quotationLens.manualQuotation |> get) &&
      !(quotationLens.sourceQuotationId |> get),
    [authorizations?.hasRightToRecomputeQuotation, globalReadOnly, quotationLens.manualQuotation, quotationLens.sourceQuotationId]
  );

  // #endregion

  return (
    <>
      {isDirty && <Prompt message={t('LeavingWithoutSaving')} />}
      <ItemComponent
        quotationLens={quotationLens}
        validation={validation}
        index={index}
        colorTheme={colorTheme}
        onSave={handleSave}
        onRetry={handleRetry}
        onSelect={handleSelect}
        onQuotationLockChange={handleQuotationLockChange}
        onHeaderPropertyChange={handleHeaderPropertyChange}
        onRiskSelectionChange={handleRiskSelectionChange}
        onCoefficientsSelectionChange={handleCoefficientsSelectionChange}
        onAmountChange={handleAmountChange}
        isDirty={isDirty}
        quotationSelected={selected}
        saving={saving}
        lockUpdating={lockUpdating}
        disabled={disabled}
        allowEditForNegotiation={allowEditForNegotiation}
        allowSelection={allowSelection}
        allowCustomQuotationEdit={allowCustomQuotationEdit}
        allowQuotationsLockUpdate={allowQuotationLock && !selected && !isDirty && !saving}
        allowRetry={allowRetry}
        titleRef={titleRef}
        messageRef={messageRef}
        showTitleTooltip={showTitleTooltip}
        showMessageTooltip={showMessageTooltip}
        onEditCustomQuotation={handleEditCustomQuotation}
        allowEmptySelection={authorizations?.hasRightToSaveEmptyAdditionalData}
        isLastQuotationModified={lastModifiedQuotationId === (quotationLens.quotationId |> get)}
      />
      {Backdrop()}
    </>
  );
};

QuotationContainer.propTypes = {
  index: PropTypes.number,
  data: PropTypes.object,
  ItemComponent: PropTypes.elementType,
  selected: PropTypes.bool,
  computing: PropTypes.bool,
  addQuotationsEnabled: PropTypes.bool,
  allowQuotationsLockUpdate: PropTypes.bool,
  onEditCustomQuotation: PropTypes.func,
  onQuotationDataChanged: PropTypes.func,
  setHasHistory: PropTypes.func
};

export default QuotationContainer;
